/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — stmt-balance.ts
   buildBalanceSheet() and its helpers
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import {
  prepareStatementData,
  restoreStatementData,
} from './consolidation';
import {
  sum,
  rows,
  hasPriorData,
  computeNetIncome,
  isTrialBalanceClosed,
  tr,
  sectionHeader,
  columnHeaders,
  stmtHeader,
  cur,
  fmt,
  el,
} from './utils';
import { mergeLabels, type MergedItem } from './stmt-income';

/**
 * Builds the balance sheet HTML table with assets, liabilities, and equity sections.
 */
export function buildBalanceSheet(): void {
  prepareStatementData();
  try {
  const multi: boolean = hasPriorData();

  const totalCA: number = sum(state.currentData, 'current-assets');
  const totalNCA: number = sum(state.currentData, 'noncurrent-assets');
  const totalAssets: number = totalCA + totalNCA;
  const totalCL: number = sum(state.currentData, 'current-liab');
  const totalNCL: number = sum(state.currentData, 'noncurrent-liab');
  const totalLiab: number = totalCL + totalNCL;
  const netIncomeCur: number = computeNetIncome(state.currentData);
  // Detect if TB is closed (RE already includes current period NI) — avoids double-counting NI
  const isClosed: boolean = isTrialBalanceClosed(state.currentData);
  // For unclosed TB: equity = sum(equity) + NI (NI flows in); for closed: equity = sum(equity)
  const totalEquity: number = sum(state.currentData, 'equity') + (isClosed ? 0 : netIncomeCur);
  const totalLiabEquity: number = totalLiab + totalEquity;

  let pTotalCA = 0;
  let pTotalNCA = 0;
  let pTotalAssets = 0;
  let pTotalCL = 0;
  let pTotalNCL = 0;
  let pTotalLiab = 0;
  let pTotalEquity = 0;
  let pTotalLiabEquity = 0;
  let pNetIncomeCur = 0;
  if (multi) {
    pTotalCA = sum(state.priorData, 'current-assets');
    pTotalNCA = sum(state.priorData, 'noncurrent-assets');
    pTotalAssets = pTotalCA + pTotalNCA;
    pTotalCL = sum(state.priorData, 'current-liab');
    pTotalNCL = sum(state.priorData, 'noncurrent-liab');
    pTotalLiab = pTotalCL + pTotalNCL;
    pNetIncomeCur = computeNetIncome(state.priorData);
    const pIsClosed: boolean = isTrialBalanceClosed(state.priorData);
    pTotalEquity = sum(state.priorData, 'equity') + (pIsClosed ? 0 : pNetIncomeCur);
    pTotalLiabEquity = pTotalLiab + pTotalEquity;
  }

  let html: string = stmtHeader('Balance Sheet');
  const tableClass: string = multi ? 'stmt-table multi-period' : 'stmt-table';
  if (multi) {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-current"><col class="col-prior"></colgroup><tbody>';
  } else {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-amount"><col class="col-total"></colgroup><tbody>';
  }
  html += columnHeaders(multi);

  html += sectionHeader('ASSETS', multi);
  html += sectionHeader('Current Assets', multi);
  const caItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'current-assets'),
    multi ? rows(state.priorData, 'current-assets') : [],
    multi
  );
  caItems.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Current Assets', indent: false, currentAmt: totalCA, priorAmt: multi ? pTotalCA : null, isTotal: false, cls: '', multi });

  html += sectionHeader('Non-Current Assets', multi);
  const ncaItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'noncurrent-assets'),
    multi ? rows(state.priorData, 'noncurrent-assets') : [],
    multi
  );
  ncaItems.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Non-Current Assets', indent: false, currentAmt: totalNCA, priorAmt: multi ? pTotalNCA : null, isTotal: false, cls: '', multi });
  html += tr({ label: 'TOTAL ASSETS', indent: false, currentAmt: totalAssets, priorAmt: multi ? pTotalAssets : null, isTotal: true, cls: '', multi });

  html += sectionHeader('LIABILITIES & EQUITY', multi);
  html += sectionHeader('Current Liabilities', multi);
  const clItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'current-liab'),
    multi ? rows(state.priorData, 'current-liab') : [],
    multi
  );
  clItems.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Current Liabilities', indent: false, currentAmt: totalCL, priorAmt: multi ? pTotalCL : null, isTotal: false, cls: '', multi });

  html += sectionHeader('Non-Current Liabilities', multi);
  const nclItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'noncurrent-liab'),
    multi ? rows(state.priorData, 'noncurrent-liab') : [],
    multi
  );
  nclItems.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Non-Current Liabilities', indent: false, currentAmt: totalNCL, priorAmt: multi ? pTotalNCL : null, isTotal: false, cls: '', multi });
  html += tr({ label: 'Total Liabilities', indent: false, currentAmt: totalLiab, priorAmt: multi ? pTotalLiab : null, isTotal: false, cls: '', multi });

  html += sectionHeader("Shareholders' Equity", multi);
  const eqItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'equity'),
    multi ? rows(state.priorData, 'equity') : [],
    multi
  );
  eqItems.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  // Only show Net Income line if TB is unclosed (NI hasn't been rolled into RE yet)
  if (!isClosed) {
    html += tr({ label: 'Net Income', indent: true, currentAmt: netIncomeCur, priorAmt: multi ? pNetIncomeCur : null, isTotal: false, cls: '', multi });
  }
  html += tr({ label: 'Total Equity', indent: false, currentAmt: totalEquity, priorAmt: multi ? pTotalEquity : null, isTotal: false, cls: '', multi });

  html += tr({ label: 'TOTAL LIABILITIES & EQUITY', indent: false, currentAmt: totalLiabEquity, priorAmt: multi ? pTotalLiabEquity : null, isTotal: true, cls: '', multi });

  const diff: number = totalAssets - totalLiabEquity;
  const cols: number = multi ? 5 : 3;
  if (Math.abs(diff) > 0.01) {
    html +=
      '<tr><td colspan="' +
      cols +
      '" style="padding:12px 8px;font-size:0.8rem;color:var(--red)">' +
      'Warning: Balance sheet does not balance by ' +
      cur() +
      fmt(Math.abs(diff)) +
      '. Check your entries.' +
      '</td></tr>';
  } else {
    html +=
      '<tr><td colspan="' +
      cols +
      '" style="padding:12px 8px;font-size:0.8rem;color:var(--green)">' +
      'Balance sheet balances.' +
      '</td></tr>';
  }

  html += '</tbody></table>';
  const balanceEl = el('balance-statement');
  if (balanceEl) balanceEl.innerHTML = html;
  } finally { restoreStatementData(); }
}
